import { useIntl } from "react-intl";

import { type ApiFilter } from "iqf-web-ui/data-table";
import { useEvidenceNameConf } from "iqf-web-ui/evidence";

import { ENV } from "@/env";

import { caseStateValueSchema } from "./case-state/schemas/case-state-value";
import { caseTabsSchema } from "./tabs/schema";

const caseUrl = `/case`;
const caseApi = `${ENV.VITE_API_BASE_PATH}/case`;

export const caseConf = {
  url: caseUrl,
  api: caseApi,

  getListUrl: (name = "all") => `${caseUrl}/${name}`,
  getCaseDetailUrl: (caseId: string, tab?: string) =>
    `${caseUrl}/detail/${caseId}/${tab ?? caseTabsSchema.Enum["base-info"]}`,
  getCaseDetailApi: (caseId: string) => `${caseApi}/${caseId}`,
  getActivitiesApi: (caseId: string) => `${caseApi}/${caseId}/activities`,
  getOpenedActivityApi: (caseId: string) =>
    `${caseApi}/${caseId}/activities/opened`,
  getAdActaApi: (caseId: string) => `${caseApi}/${caseId}/ad-acta`,
  getTerminationApi: (caseId: string) => `${caseApi}/${caseId}/termination`,
  getReopenApi: (caseId: string) => `${caseApi}/${caseId}/reopen`,
  getTerminationEligibilityApi: (caseId: string) =>
    `${caseApi}/${caseId}/eligibility/termination`,
  getReopenEligibilityApi: (caseId: string) =>
    `${caseApi}/${caseId}/eligibility/reopen`,
  getAdActaEligibilityApi: (caseId: string) =>
    `${caseApi}/${caseId}/eligibility/ad-acta`,
  getMonitoringReviewEligibilityApi: (caseId: string) =>
    `${caseApi}/${caseId}/eligibility/monitoring-review`,
  getMonitoringReviewOriginalMonitoringApi: (caseId: string) =>
    `${caseApi}/${caseId}/monitoring-review/original-monitoring`,
  getMonitoringReviewApi: (caseId: string) =>
    `${caseApi}/${caseId}/monitoring-review`,
  getMonitoringReviewCopyOriginalDocumentsApi: (caseId: string) =>
    `${caseApi}/${caseId}/monitoring-review/original-documents/copy`,
  getMonitoringReviewFollowUpCaseApi: (caseId: string) =>
    `${caseApi}/${caseId}/monitoring-review/follow-up-case`,
  getProceedingProposalUrl: (caseId: string) =>
    `${caseUrl}/${caseId}/proceeding-proposal`,
  getProceedingProposalApi: (caseId: string) =>
    `${caseApi}/${caseId}/proceeding-proposal`,
  getProceedingProposalEligibilityApi: (caseId: string) =>
    `${caseApi}/${caseId}/eligibility/proceeding-proposal`,
};

export const caseConfs = {
  own: {
    name: "own",
    id: "OWN_OPEN_CASES",
    api: caseConf.api,
    url: caseConf.getListUrl("own"),
  },
  all: {
    name: "all",
    id: "ALL_CASES",
    api: caseConf.api,
    url: caseConf.getListUrl("all"),
  },
  "in-progress": {
    name: "in-progress",
    id: "IN_PROGRESS",
    api: caseConf.api,
    url: caseConf.getListUrl("in-progress"),
  },
  ended: {
    name: "ended",
    id: "ENDED",
    api: caseConf.api,
    url: caseConf.getListUrl("ended"),
  },
  opened: {
    name: "opened",
    id: "OPENED",
    api: caseConf.api,
    url: caseConf.getListUrl("opened"),
  },
  department: {
    name: "department",
    id: "DEPARTMENT_OPEN_CASES",
    api: caseConf.api,
    url: caseConf.getListUrl("department"),
  },
} as const;

const openCasePrefilters: ApiFilter[] = [
  {
    operation: "IN",
    field: "state.id",
    values: [
      caseStateValueSchema.Enum.NEW,
      caseStateValueSchema.Enum.IN_PROGRESS,
      caseStateValueSchema.Enum.REOPENED,
    ],
  },
];

export const casePrefilters = {
  own: openCasePrefilters,
  department: openCasePrefilters,
  all: [],
  "in-progress": [
    {
      operation: "EQ",
      field: "state.id",
      value: "IN_PROGRESS",
    },
  ],
  ended: [
    {
      operation: "EQ",
      field: "state.id",
      value: "ENDED",
    },
  ],
  opened: [
    {
      operation: "EQ",
      field: "state.id",
      value: "REOPENED",
    },
  ],
} satisfies Record<keyof typeof caseConfs, ApiFilter[]>;

export function useCaseTitles() {
  const intl = useIntl();

  return {
    all: intl.formatMessage({
      id: "case.title.all",
      defaultMessage: "Všechny případy",
    }),
    "in-progress": intl.formatMessage({
      id: "case.title.in-progress",
      defaultMessage: "Probíhající případy",
    }),
    ended: intl.formatMessage({
      id: "case.title.ended",
      defaultMessage: "Ukončené případy",
    }),
    opened: intl.formatMessage({
      id: "case.title.opened",
      defaultMessage: "Znovuotvorené případy",
    }),
    own: intl.formatMessage({
      id: "case.title.own",
      defaultMessage: "Vlastné otvorené prípady",
    }),
    department: intl.formatMessage({
      id: "case.title.department",
      defaultMessage: "Otvorené prípady útvaru",
    }),
  };
}

export function useCaseTitle() {
  const titles = useCaseTitles();
  const conf = useEvidenceNameConf(caseConfs);

  return titles[conf.name];
}
